import { t } from '../i18n/index.ts'
import { shouldRunSilentWebUpdate, silentReloadForWaitingWorker } from '../app/silentAutoUpdate.ts'

const DISMISS_KEY = 'zion_pwa_update_dismissed'
let waitingWorker: ServiceWorker | null = null
let bannerEl: HTMLElement | null = null
let wired = false
let reloading = false

function isDismissedTemporarily(): boolean {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const until = Number(raw)
    return Number.isFinite(until) && Date.now() < until
  } catch {
    return false
  }
}

function dismissTemporarily(): void {
  try {
    // Nascondi per questa sessione (fino a reload / 6h)
    sessionStorage.setItem(DISMISS_KEY, String(Date.now() + 6 * 60 * 60 * 1000))
  } catch {
    /* ignore */
  }
  hidePwaUpdateBanner()
}

function ensureBanner(): HTMLElement {
  let el = document.getElementById('pwa-update-banner')
  if (el) return el

  el = document.createElement('div')
  el.id = 'pwa-update-banner'
  el.className = 'pwa-update-banner hidden'
  el.setAttribute('role', 'status')
  el.innerHTML = `
    <div class="pwa-update-banner-inner">
      <p class="pwa-update-banner-text" id="pwa-update-text"></p>
      <div class="pwa-update-banner-actions">
        <button type="button" class="pwa-update-btn" id="pwa-update-now"></button>
        <button type="button" class="pwa-update-dismiss" id="pwa-update-dismiss" aria-label="Close">×</button>
      </div>
    </div>
  `
  document.body.appendChild(el)

  el.querySelector('#pwa-update-now')!.addEventListener('click', () => {
    const btn = el!.querySelector('#pwa-update-now') as HTMLButtonElement
    btn.disabled = true
    const target = waitingWorker
    if (target) {
      target.postMessage({ type: 'SKIP_WAITING' })
    } else {
      void navigator.serviceWorker.getRegistration('/app/').then((reg) => {
        reg?.waiting?.postMessage({ type: 'SKIP_WAITING' })
      })
    }
    // controllerchange farà il reload; fallback se non arriva
    window.setTimeout(() => {
      if (!reloading) window.location.reload()
    }, 2500)
  })

  el.querySelector('#pwa-update-dismiss')!.addEventListener('click', () => {
    dismissTemporarily()
  })

  bannerEl = el
  return el
}

export function showPwaUpdateBanner(worker?: ServiceWorker | null): void {
  // Capacitor nativo: niente banner update PWA / “Installa”
  try {
    const Cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    if (Cap?.isNativePlatform?.()) return
  } catch {
    /* ignore */
  }
  if (isDismissedTemporarily()) return
  if (worker) waitingWorker = worker

  const el = ensureBanner()
  const text = el.querySelector('#pwa-update-text')
  const btn = el.querySelector('#pwa-update-now')
  if (text) text.textContent = t('pwa_update_banner_text')
  if (btn) btn.textContent = t('pwa_update_banner_btn')
  el.classList.remove('hidden')
}

export function hidePwaUpdateBanner(): void {
  const el = bannerEl || document.getElementById('pwa-update-banner')
  el?.classList.add('hidden')
}

function onUpdateReady(worker: ServiceWorker): void {
  if (shouldRunSilentWebUpdate()) {
    silentReloadForWaitingWorker(worker)
    return
  }
  waitingWorker = worker
  showPwaUpdateBanner(worker)
}

const attachedRegs = new WeakSet<ServiceWorkerRegistration>()

function attachRegistration(reg: ServiceWorkerRegistration): void {
  if (attachedRegs.has(reg)) {
    if (reg.waiting && navigator.serviceWorker.controller) onUpdateReady(reg.waiting)
    return
  }
  attachedRegs.add(reg)

  if (reg.waiting && navigator.serviceWorker.controller) {
    onUpdateReady(reg.waiting)
  }

  // Prima installazione (nessun controller): attiva subito senza banner
  if (reg.waiting && !navigator.serviceWorker.controller) {
    reg.waiting.postMessage({ type: 'SKIP_WAITING' })
  } else if (reg.waiting && navigator.serviceWorker.controller && shouldRunSilentWebUpdate()) {
    silentReloadForWaitingWorker(reg.waiting)
  }

  reg.addEventListener('updatefound', () => {
    const worker = reg.installing
    if (!worker) return
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        onUpdateReady(worker)
      }
    })
  })

  const check = (): void => {
    void reg.update().catch(() => {})
  }
  window.setInterval(check, 15 * 60 * 1000)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })
}

/**
 * Ascolta updatefound / waiting SW e mostra banner "Aggiorna ora".
 * Reload su controllerchange dopo SKIP_WAITING.
 */
export function initPwaUpdateBanner(reg?: ServiceWorkerRegistration | null): void {
  if (!('serviceWorker' in navigator)) return

  if (!wired) {
    wired = true
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      try {
        sessionStorage.removeItem(DISMISS_KEY)
      } catch {
        /* ignore */
      }
      window.location.reload()
    })
  }

  if (reg) {
    attachRegistration(reg)
    return
  }

  void navigator.serviceWorker.getRegistration('/app/').then((r) => {
    if (r) attachRegistration(r)
  })
}
